/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
// utils
import {
  getChartColorsSubset,
  revenueChartZAxisColor,
  formatNumber,
  formatTableDate,
  formatXAxisDates,
  getChangeRateDirection,
  getEnabledRollUps,
  sortMetricData
} from '../../../../utils/charts'
import { getStartAndEndDatesOfWeek } from '../../../../utils/date'
import { PRODUCT_TYPES } from '../../../../utils/consts'
// material
import { SingleSelectDropDown, countries as countriesMap } from '@insticator/insticator-ui'
import PageContent from '../../../../material/structure/pagecontent/pagecontent'
import LineChart from '../../../../material/linechart/linechart'
import ChartLegend from '../../../../material/chartlegend/chartlegend'
import CardToggles from '../../../../material/cardwrapper/cardtoggles/cardtoggles'
import CardToggle from '../../../../material/cardwrapper/cardtoggles/cardtoggle/cardtoggle'
import BarChart from '../../../../material/barchart/barchart'
import DetailsTable from '../../../../material/detailstable/detailstable'
import ChartReportSection from '../../../../material/chartreportsection/chartreportsection'
// styles
import injectSheet from 'react-jss'
import styles from './revenue_beta_style'
// redux
import {
  setDatePeriod,
  resetDatePeriod,
  resetDimension,
  setDatePeriodForAll,
  setDimension,
  setDimensionForAll,
  refresh,
  resetSecondDimension
} from '../../../../state/slices/revenue_beta'
// static
import {
  DIMENSION_DROPDOWN_OPTIONS,
  BAR_CHART,
  MONETIZATION_TYPE_BETA,
  AVG_CPM,
  AVG_RPM,
  AD_UNIT,
  VIEWABILITY,
  INITIAL_VIEWABILITY,
  REFRESH_VIEWABILITY,
  VIDEO_COMPLETION_RATE,
  DISABLED_SECONDARY_DIMENSIONS,
  IMPRESSION_PER_PAGEVIEW,
  LINE_CHART,
  METRIC_UNIT
} from '../consts'

import { callRelevantApi, isPercentageMetric } from '../../helperfunction'
import {SITES} from "../../reportbuilder/monetizationform/consts";

/* ========== ~~~~~~~~~~ REVENUE VISUALIZATION ~~~~~~~~~~ ========== */
export default injectSheet(styles)((props) => {
  const { dimension, initialMetric, startDate, endDate, compareTo } = props.params
  const { cardtogglesList, chartType, zAxisId, zAxisLabel, allowedDimensions, title, tooltipContent } = props

  const [selectedPeriodX, setSelectedPeriodX] = useState(null)
  const [metricToggle, changeMetricToggle] = useState(initialMetric)
  const [selectedPeriod, setSelectedPeriod] = useState('daily')
  const [selectedDimension, setSelectedDimension] = useState(dimension)

  const [chartLegends, setChartLegends] = useState([])
  const [showTotal, setShowTotal] = useState(false)
  const [enabledRollups, setEnabledRollups] = useState([])

  // secondary dimension, set by clicking on a legend
  const [additionalFilter, setAdditionalFilter] = useState(null)
  const [hoveredLegend, setHoveredLegend] = useState(null)

  const currentPage = useSelector(state => state.app.currentPage)
  const sitesAndAppsUUIDs = useSelector((state) => state.account.sitesAndAppsUUIDs)
  const { status } = useSelector((state) => state.revenueBeta[metricToggle])
  const videoStatus = useSelector((state) => state.revenueBeta[VIDEO_COMPLETION_RATE].status)
  const pageviewStatus = useSelector((state) => state.revenueBeta[IMPRESSION_PER_PAGEVIEW].status)
  const previouslySelectedPeriod = useSelector((state) => state.revenueBeta.selectedPeriod[metricToggle])
  const metricUnit = useSelector((state) => state.revenueBeta.metricUnit[metricToggle])
  const dimensionState = useSelector((state) => state.revenueBeta.dimension[metricToggle])
  const details = useSelector((state) => state.revenueBeta)
  const { filters } = useSelector((state) => state.app)
  const { account } = useSelector((state) => state)
  const { countries, devices, sites, sitesByType } = filters

  const dispatch = useDispatch()

  const changeType = filters.compareTo

  const metricUnits = { ...METRIC_UNIT }

  const metric = metricToggle
  const period = selectedPeriod.toUpperCase()
  const selectedChartData = details[metric][period]
  const products = sortMetricData(selectedChartData, details[metric].TOTAL)
  const dates = selectedChartData.TOTAL
  const overview = details

  const metricsFetched = cardtogglesList.map(c => c.id)
  const noDataInEveryMetric = () => metricsFetched.every(metric => {
    return overview[metric].TOTAL.TOTAL.data === 0
  })
  const showTotalAsAvg = [AVG_RPM, AVG_CPM, VIEWABILITY, INITIAL_VIEWABILITY, REFRESH_VIEWABILITY].includes(metricToggle)
  const cardToggleTitle = cardtogglesList.find(c => c.id === metricToggle).label
  const selectedMetricLabel = showTotalAsAvg ? cardToggleTitle : ''

  const removeTotalFromColors = Object.keys(dates).length === 0 || showTotalAsAvg ? 0 : 1
  const revenueChartTableColors = getChartColorsSubset(Object.keys(selectedChartData).length - removeTotalFromColors)

  const productKeys = Object.keys(products)
  const chartLegendIds = chartLegends.map(l => l.id)
  const isChartLegendInSync = productKeys.length === chartLegendIds.length && productKeys.every(k => chartLegendIds.includes(k))

  useEffect(() => {
    dispatch(resetDatePeriod())
    dispatch(resetDimension())
    return dispatch(refresh())
  }, [currentPage])

  useEffect(() => {
    const { first, second } = dimensionState
    if (first && !second && allowedDimensions.includes(first)) setSelectedDimension(first)
  }, [dimensionState])

  useEffect(() => {
    if (selectedDimension && startDate && endDate) {
      const params = JSON.parse(JSON.stringify(props.params))
      params.dimension = selectedDimension

      if (!params.filters[SITES]) params.filters[SITES] = [...Object.keys(sitesAndAppsUUIDs || {})]
      if (additionalFilter) params.filters[additionalFilter.filter] = [additionalFilter.value]

      setHoveredLegend(null)
      if (params.metrics.includes(AVG_RPM) && params.metrics.includes(AVG_CPM)) {
        if (metric === AVG_RPM && selectedDimension === AD_UNIT) {
          changeMetricToggle(AVG_CPM);
        }
        const avgRPMParams = { ...params };
        avgRPMParams.metrics = [AVG_RPM];
        dispatch(callRelevantApi(avgRPMParams));
        const avgCPMParams = { ...params };
        avgCPMParams.metrics = [AVG_CPM];
        dispatch(callRelevantApi(avgCPMParams));
      } else dispatch(callRelevantApi(params));

    }
  }, [selectedDimension, countries, compareTo, devices, sites, sitesByType, startDate, endDate, additionalFilter])

  useEffect(() => {
    if (zAxisId === VIDEO_COMPLETION_RATE || (zAxisId === IMPRESSION_PER_PAGEVIEW && status === 'fulfilled')) {
      const params = JSON.parse(JSON.stringify(props.params));
      params.metrics = [zAxisId];
      dispatch(callRelevantApi(params));
    }
  }, [status, zAxisId])

  useEffect(() => {
    if (status === 'fulfilled' && chartLegends.length === 0) updateChartLegends()
  }, [status, chartLegends])

  useEffect(() => {
    if (metric) updateChartLegends()
  }, [metric])

  useEffect(() => {
    if (showTotal) updateChartLegends()
  }, [showTotal])

  useEffect(() => {
    if ((zAxisId === VIDEO_COMPLETION_RATE || zAxisId === IMPRESSION_PER_PAGEVIEW) &&
      (videoStatus === 'fulfilled' || pageviewStatus === 'fulfilled')) updateChartLegends()
  }, [zAxisId, videoStatus, pageviewStatus])

  useEffect(() => {
    if (status === 'fulfilled' && enabledRollups.length === 0) {
      const rollups = getEnabledRollUps(details.TOTAL)
      setEnabledRollups(rollups)
      if (rollups.length > 0) { setSelectedPeriod(previouslySelectedPeriod || rollups[0]) } else { setSelectedPeriod('monthly') }
    } else {
      setEnabledRollups([])
      setSelectedPeriod(previouslySelectedPeriod || 'daily')
      setChartLegends([])
    }
  }, [status])

  useEffect(() => {
    if (previouslySelectedPeriod && enabledRollups.length > 0) setSelectedPeriod(previouslySelectedPeriod)
  }, [previouslySelectedPeriod])

  useEffect(() => {
    if (status === 'fulfilled') {
      if (!noDataInEveryMetric()) {
        const metricHasData = metricsFetched.find(metric => {
          return overview[metric].TOTAL.TOTAL.data !== 0
        })
        changeMetricToggle(metricHasData)
      }
    }
  }, [status])

  useEffect(() => {
    // cleanup local component states
    return () => {
      setChartLegends([])
      setHoveredLegend(null)
    }
  }, [])

  const computeTableTotal = (products, checkedOnly = true) => {
    if (showTotalAsAvg) {
      const checked = products.filter(p => p.id !== 'TOTAL' && p.checked === true)
      const dividendSum = checked.map(p => p.dividend).reduce((a, b) => a + b, 0)
      const divisorSum = checked.map(p => p.divisor).reduce((a, b) => a + b, 0)
      return Number(dividendSum / divisorSum).toFixed(2)
    }
    // Compute total depending on the selected products
    return products
      .filter(product => product.id !== 'TOTAL' && product.id !== zAxisId && (checkedOnly ? (product.checked === true) : true))
      .map(product => product.value)
      .reduce((prev, current) => prev + current, 0)
  }

  const dimensionMap = [
    ...PRODUCT_TYPES,
    ...Object.keys(selectedChartData).map((dimension, i) => {
      if (account.sitesAndAppsUUIDs[dimension]) return { id: dimension, name: account.sitesAndAppsUUIDs[dimension], color: revenueChartTableColors[i] }
      if (countriesMap[dimension]) return { id: dimension, name: countriesMap[dimension], color: revenueChartTableColors[i] }
      else return { id: dimension, name: dimension, color: revenueChartTableColors[i] }
    })
  ]

  // can use for both line and bar charts
  const getChartColor = (id, index) => {
    if (hoveredLegend !== null) {
      if (index === hoveredLegend) return getDimensionInfo(id, index)?.color
      else return '#DFDFDF'
    }
    return getDimensionInfo(id, index)?.color
  }

  const getDimensionInfo = (dimension, i) => {
    const curr = dimensionMap.filter(d => dimension === d.id)
    if (dimension === zAxisId) return { id: dimension, name: zAxisLabel, color: revenueChartZAxisColor }
    if (curr.length > 0) {
      const mapped = curr[0]
      mapped.color = revenueChartTableColors[i]
      return mapped
    } else {
      return {
        id: dimension,
        name: dimension,
        color: revenueChartTableColors[i]
      }
    }
  }

  const updateChartLegends = () => {
    const productArr = Object.keys(products).map((id, i) => ({
      ...getDimensionInfo(id, i),
      value: overview[metric].TOTAL[id].data,
      changeRate: overview[metric].TOTAL[id].changeRate,
      dividend: overview[metric].TOTAL[id].dividend,
      divisor: overview[metric].TOTAL[id].divisor,
      checked: (chartLegends.length > 0 && isChartLegendInSync) ? chartLegends.filter(legend => (legend.id === id))[0].checked : true
    }))

    // Update total depending on selected products
    const totalValueIndex = productArr.map(product => product.id).indexOf('TOTAL')
    productArr[totalValueIndex] = {
      ...productArr[totalValueIndex],
      value: computeTableTotal(productArr, true)
    }
    setChartLegends(productArr)
  }

  const getBarChartKeys = () => [...chartLegends].map((product, i) => getDimensionInfo(product.id, i)?.name)

  const getBarChartColors = () => [...chartLegends].map((product, i) => {
    return getChartColor(product.id, i)
  })

  const renderChartLegends = () => {
    const totalValueIndex = chartLegends.map(product => product.id).indexOf('TOTAL')
    const totalValue = computeTableTotal(chartLegends, false)

    return chartLegends.map((product, index) => {
      const multiplier = metricUnit === 'percentage' ? 10 : 100
      const productPercentage = totalValue > 0 ? ((product.value ? product.value : 0) / totalValue) * multiplier : 0
      const productPercentageText = totalValue && (product.value != totalValue) && ![AVG_RPM, AVG_CPM, VIEWABILITY].includes(metric) ? '(' + formatNumber(productPercentage, 'percent') + ')' : ''

      if (product.id === 'TOTAL' && chartLegends.length === 2) return null
      if (product.id === 'TOTAL' && showTotalAsAvg) product.name = selectedMetricLabel

      return (
        <ChartLegend
          key={product.id}
          title={`${product.name} ${chartLegends.length > 2 && !['TOTAL', VIDEO_COMPLETION_RATE, IMPRESSION_PER_PAGEVIEW].includes(product.id) ? productPercentageText : ''}`}
          checkboxColor={(product.id === 'TOTAL' && !showTotalAsAvg) ? '#fff' : getChartColor(product.id, index)}
          checked={product.checked}
          onCheck={checked => {
            const legends = [...chartLegends]
            legends[index] = {
              ...legends[index],
              checked
            }

            legends[totalValueIndex] = {
              ...legends[totalValueIndex],
              value: computeTableTotal(legends)
            }

            setChartLegends(legends)
          }}
          handleOnMouseEnter={() => setHoveredLegend(index)}
          handleOnMouseLeave={() => setHoveredLegend(null)}
          isGrayedOut={hoveredLegend !== null && hoveredLegend !== index}
          value={formatNumber(product.value, metricUnit)}
          changeDirection={product.changeRate < 0 ? 'negative' : 'positive'}
          change={formatNumber(product.changeRate ? product.changeRate : 0, 'percent')}
          changeType={changeType}
          metricType={'monetization'}
          handleLegendClick={() => {
            if (!DISABLED_SECONDARY_DIMENSIONS.includes(product.id)) {
              setAdditionalFilter({
                value: product.id,
                filter: selectedDimension,
                name: product.name
              })
              cardtogglesList.forEach(card => {
                dispatch(setDimension({ metric: card.id, dimension: product.id, order: 'second' }))
              })
            }
          }}
          isLegendClickable={!additionalFilter && !DISABLED_SECONDARY_DIMENSIONS.includes(product.id)}
        />
      )
    })
  }

  const convertWeeklyDateToStartAndEnd = (date) => {
    if (period === 'WEEKLY') {
      const dateArr = date.split('-')
      const year = dateArr[0]
      const week = dateArr[1]
      return getStartAndEndDatesOfWeek(week, year)
    } else return ({ startDate: null, endDate: null })
  }

  const transformBarChartData = () => {
    let barChartData = []
    if (!isChartLegendInSync) return barChartData
    barChartData = Object.keys(dates).map(date => {
      const dateLength = Object.keys(dates).length
      const formattedDate = formatXAxisDates(date, period, dateLength, 10, true)
      const item = { date: formattedDate }
      let total = 0
      const { startDate, endDate } = convertWeeklyDateToStartAndEnd(date)
      Object.keys(products).forEach((id, i) => {
        const checkedProducts = chartLegends.filter(productType => productType.checked === true)
        if (id !== 'TOTAL' && checkedProducts.map(product => product.id).indexOf(id) >= 0) {
          const itemName = getDimensionInfo(id, i)?.name === zAxisLabel ? 'z' : getDimensionInfo(id, i)?.name
          item[itemName] = products[id] && products[id][date] ? products[id][date].data : 0
          item[`${getDimensionInfo(id, i).name}Color`] = getDimensionInfo(id, i).color
          item.origDate = date
          item.formattedTableDate = formatTableDate(date, period, startDate, endDate)
          if (id !== zAxisId) total += item[getDimensionInfo(id, i).name]
        }
        item.Total = total
      })
      return item
    })
    return barChartData
  }

  const transformLineChartData = () => {
    const lineChartData = []
    const recalculatedAvg = {}

    if (!isChartLegendInSync) return lineChartData

    const checkedProducts = chartLegends.filter(product => product.checked === true).map(product => product.id)
    const checkedData = Object.fromEntries(Object.entries(products).filter(([key]) => checkedProducts.includes(key)))

    checkedProducts.forEach(product => recalculatedAvg[product] = {})

    if (checkedData.hasOwnProperty('TOTAL')) {
      Object.keys(checkedData.TOTAL).forEach(date => {
        let dividendSum = 0
        let divisorSum = 0
        checkedProducts.forEach(product => {
          if (product === 'TOTAL') return

          recalculatedAvg[product][date] = checkedData[product][date].data
          dividendSum += checkedData[product][date].dividend ? checkedData[product][date].dividend : 0
          divisorSum += checkedData[product][date].divisor ? checkedData[product][date].divisor : 0
        })
        recalculatedAvg.TOTAL[date] = divisorSum === 0 ? 0 : Number(dividendSum / divisorSum).toFixed(2)
      })
    }

    const dateLength = products && products.TOTAL ? Object.keys(products.TOTAL).length : 10
    Object.keys(products).forEach((id, i) => {
      const checkedProducts = chartLegends.filter(product => product.checked === true)
      if (id === 'TOTAL' && !showTotalAsAvg) return
      if (chartLegends.length === 2 && id === 'TOTAL') return

      const items = []

      if (checkedProducts.map(product => product.id).indexOf(id) >= 0) {
        Object.keys(products[id]).forEach((date, index) => {
          const { startDate, endDate } = convertWeeklyDateToStartAndEnd(date)
          // Use original data if only 1 dimension/metric checked or 'Avg XXX' line is unchecked
          const value = checkedProducts.length === 1 || !checkedData.hasOwnProperty('TOTAL')
            ? products[id][date].data
            : recalculatedAvg[id][date]
          // To allow duplicate x-axis labels
          let spaces = ''
          for (let i = 0; i < index; i++) spaces += ' '
          items.push({
            x: formatXAxisDates(date, period, dateLength, 10).concat(spaces),
            y: metricUnit === 'percentage' ? value * 100 : value,
            origDate: date,
            formattedDate: formatTableDate(date, period, startDate, endDate),
            productName: getDimensionInfo(id, index).name
          })
        })
      }
      lineChartData.push({
        id,
        color: getChartColor(id, i),
        data: items
      })
    })
    return lineChartData
  }

  const computeDateTotal = date => {
    const isAllProductsSelected = chartLegends.filter(legend => legend.checked).length === chartLegends.length
    if (isAllProductsSelected) return selectedChartData.TOTAL[date].data

    const products = chartLegends.filter(legend => legend.checked && legend.id !== 'TOTAL').map(product => product.id)
    const isOneProductSelected = chartLegends.filter(legend => legend.checked && legend.id !== 'TOTAL').length === 1
    if (isOneProductSelected) return selectedChartData[products[0]][date].data

    let dividendSum = 0
    let divisorSum = 0
    products.forEach(productId => {
      const dateDetails = selectedChartData[productId][date]
      dividendSum += dateDetails.dividend ? dateDetails.dividend : 0
      divisorSum += dateDetails.divisor ? dateDetails.divisor : 0
    })
    return Number(dividendSum / divisorSum).toFixed(2)
  }

  const handleHoverOnLineChart = lineChartData => {
    const date = lineChartData.data ? lineChartData.data.origDate : lineChartData.origDate
    const legends = [...chartLegends]
    Object.keys(products).forEach(id => {
      let value = 0
      let changeRate = 0
      const productDetails = products[id] && products[id][date] ? products[id][date] : null
      if (productDetails) {
        value = productDetails.data ? productDetails.data : 0
        changeRate = productDetails.changeRate ? productDetails.changeRate : 0
      }

      const legendIndex = chartLegends.map(product => product.id).indexOf(id)
      legends[legendIndex] = {
        ...legends[legendIndex],
        value,
        changeRate
      }
    })

    // Update total depending on selected products
    const totalValueIndex = chartLegends.map(product => product.id).indexOf('TOTAL')
    legends[totalValueIndex] = {
      ...legends[totalValueIndex],
      value: computeDateTotal(date)
    }

    setChartLegends(legends)
    setSelectedPeriodX(`${lineChartData.data ? lineChartData.data.formattedDate : lineChartData.formattedDate}`)
    setShowTotal(false)
  }

  const handleHoverOnBarChart = barData => {
    const date = barData.data ? barData.data.origDate : barData.origDate
    const legends = [...chartLegends]
    Object.keys(products).forEach(id => {
      let value = 0
      let changeRate = 0
      const productDetails = products[id] && products[id][date] ? products[id][date] : null
      if (productDetails) {
        value = productDetails.data ? productDetails.data : 0
        changeRate = productDetails.changeRate ? productDetails.changeRate : 0
      }

      const legendIndex = chartLegends.map(product => product.id).indexOf(id)
      legends[legendIndex] = {
        ...legends[legendIndex],
        value,
        changeRate
      }
    })

    // Update total depending on selected products
    const totalValueIndex = chartLegends.map(product => product.id).indexOf('TOTAL')
    legends[totalValueIndex] = {
      ...legends[totalValueIndex],
      value: computeTableTotal(legends)
    }

    setChartLegends(legends)
    setSelectedPeriodX(`${barData.data ? barData.data.formattedTableDate : barData.formattedTableDate}`)
  }

  const handleMouseLeave = () => {
    setShowTotal(true)
    setSelectedPeriodX('')
  }

  const availableDimensions = DIMENSION_DROPDOWN_OPTIONS
    .filter(dimension => allowedDimensions.includes(dimension.value))
    .filter(dimension => dimension.value !== additionalFilter?.filter)

  const renderSidebar = () => {
    const { filter, value, name } = additionalFilter || {}
    const filterLabel = filter && DIMENSION_DROPDOWN_OPTIONS.find(opt => opt.value === filter).name
    const valueLabel = name

    const handleBackOut = () => {
      dispatch(resetSecondDimension(metric))
      dispatch(setDimensionForAll(filter))
      setSelectedDimension(filter)
      setAdditionalFilter(null)
    }
    /* we don't want to show AD_UNIT when we have AVG_RPM, so removing AD_UNIT from available dimensions in case of AVG_RPM */
    const finalAvailableDimensions = (metric === AVG_RPM ? availableDimensions.filter(item => item.value != AD_UNIT) : availableDimensions)
    return <div className={props.classes.legendsList}>
      {(filter && value) && <div className={props.classes.dimensionPagination}>
        <span onClick={handleBackOut}>{filterLabel}</span>
        <span>{` > ${valueLabel}`}</span>
      </div>}
      <div className={props.classes.dimensionLabel}>{additionalFilter ? 'Second Dimension' : 'Dimension'}</div>
      <SingleSelectDropDown
        klass={props.classes.dimensionDropdown}
        name={'Choose a Dimension'}
        onSelect={opt => {
          dispatch(setDatePeriod({ metric, period: period.toLowerCase() }))
          if (additionalFilter) {
            setSelectedDimension(opt.value)
          } else {
            setSelectedDimension(opt.value)
            dispatch(setDimensionForAll(opt.value))
          }
        }}
        items={finalAvailableDimensions}
        selected={finalAvailableDimensions.find(option => option.value === selectedDimension)}
        searchable={false}
        paginated={false}
      />
      {renderChartLegends()}
    </div>
  }

  const monetizationBarChart = {
    indexBy: 'date',
    data: chartType === BAR_CHART ? transformBarChartData() : [],
    keys: getBarChartKeys(),
    legends: renderSidebar(),
    colors: getBarChartColors()
  }

  const cardtoggles = (
    <CardToggles>
      {cardtogglesList.filter(card => {
        return !(overview[card.id].TOTAL.TOTAL.data <= 0 && (card.id === AVG_RPM && selectedDimension === AD_UNIT))/* don't show particular card for which data is 0 and if card is AVG_RPM with selected dimension as AD UNIT */
      }).map(card => <CardToggle
        label={card.label}
        compareType={changeType}
        metricType={'monetization'}
        handleClick={() => {
          if (card.id != metricToggle) {
            changeMetricToggle(card.id)
          }
        }}
        percentChange={{
          value: formatNumber(overview[card.id].TOTAL.TOTAL.changeRate, 'percent'),
          direction: getChangeRateDirection(overview[card.id].TOTAL.TOTAL.changeRate)
        }}
        selected={metricToggle === card.id}
        value={formatNumber(overview[card.id].TOTAL.TOTAL.data, metricUnits[card.id])}
      />)
      }
    </CardToggles>
  )

  const barchart =
    <BarChart
      indexBy={monetizationBarChart.indexBy}
      colors={monetizationBarChart.colors}
      keys={monetizationBarChart.keys}
      data={monetizationBarChart.data}
      period={selectedPeriod}
      metricType={metricUnit}
      onHover={handleHoverOnBarChart}
      onMouseLeave={handleMouseLeave}
      onMouseLeavePerBar={handleMouseLeave}
      reset={showTotal}
      yType={metricUnit}
      zAxisId={zAxisId ? zAxisLabel : null}
      zAxisColor={revenueChartZAxisColor}
    />

  const formattedData = chartType == LINE_CHART ? transformLineChartData() : []
  const hasSingleData = () => {
    let singleData = false
    formattedData.forEach(item => {
      if (item.data.length === 1) singleData = true
    })
    return singleData
  }

  const lineChart =
    <LineChart
      enablePoints={hasSingleData()}
      data={formattedData}
      onHover={handleHoverOnLineChart}
      onMouseLeave={handleMouseLeave}
      reset={showTotal}
      yType={metricUnit}
      useDashedLines={true}
      period={period}
      isPercentageData={isPercentageMetric(metric)}
    />

  const detailtable = (
    <DetailsTable
      tableType={MONETIZATION_TYPE_BETA}
      dimensionMap={dimensionMap}
      adsType={metric}
      period={period}
      data={details[metric]}
      csvFileName={cardToggleTitle}
      metricUnit={metricUnit}
      totalAsAvgLabel={selectedMetricLabel}
      currentMetric={metric}
      zAxisId={props.zAxisId}
    />
  )

  const isNoData = () => {
    return (status === 'rejected') ||
      (status === 'fulfilled' && noDataInEveryMetric())
  }

  return (
    <PageContent>
      <ChartReportSection
        legendTitle={selectedPeriodX}
        title={title}
        tooltipContent={tooltipContent}
        cardtoggles={cardtoggles}
        chart={chartType === BAR_CHART ? barchart : lineChart}
        loading={status === 'pending'}
        noData={isNoData()}
        enabledPeriods={enabledRollups}
        chartLegend={monetizationBarChart.legends}
        handleSelectPeriod={periodData => {
          dispatch(setDatePeriodForAll(periodData))
          handleMouseLeave()
        }}
        selectedPeriod={selectedPeriod}
        detailTable={detailtable}
      />
    </PageContent>
  )
})
